import { Fragment, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ChevronRight, Pencil, Plus } from 'lucide-react'

import type { WarehousePositionLotType } from '@/lib/api/warehouse-position-api'
import { PageBackButton } from '@/components/common/PageBackButton'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { defineListFilters } from '@/components/common/filters/core'
import { selectFilter } from '@/components/common/filters/select-filter'
import { textFilter } from '@/components/common/filters/text-filter'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { PositionStockRows } from '@/components/warehouse-positions/position-stock-rows'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    useWarehousePositionStock,
    useWarehousePositions,
} from '@/hooks/use-warehouse-position-query'
import { m } from '@/lib/paraglide/messages'
import { twJoin } from '@/lib/utils'

/**
 * Bộ lọc khai một lần, suy ra: parser URL, mặc định, badge đếm, chip.
 *
 * Ô tìm mã trước đây nằm trong `useState` nên không vào URL — chia sẻ link hay
 * bấm Back đều mất từ khoá. `textFilter` đưa nó lên URL kèm debounce sẵn (R9).
 */
const FILTERS = defineListFilters({
    q: textFilter({
        label: m.warehouse_position_code,
        placeholder: m.warehouse_position_code_placeholder,
    }),
    lot_type: selectFilter<WarehousePositionLotType | 'all'>({
        label: m.warehouse_position_applicable_types,
        defaultValue: 'all',
        options: () => [
            { value: 'all', label: m.warehouse_position_filter_all() },
            { value: 'product', label: m.warehouse_position_type_product() },
            {
                value: 'packaging',
                label: m.warehouse_position_type_packaging(),
            },
        ],
    }),
    // Giá trị là `active`/`inactive` chứ không phải `true`/`false`: TanStack
    // phải bọc nháy chuỗi "true" để phân biệt với boolean, ra URL
    // `is_active=%22true%22` vừa xấu vừa khó gõ tay.
    is_active: selectFilter<'all' | 'active' | 'inactive'>({
        label: m.warehouse_position_status_label,
        defaultValue: 'all',
        options: () => [
            { value: 'all', label: m.warehouse_position_filter_all() },
            { value: 'active', label: m.warehouse_position_active() },
            { value: 'inactive', label: m.warehouse_position_inactive() },
        ],
    }),
})

/** Số cột của bảng — dòng tải/rỗng và dòng bung tồn đều trải hết bảng. */
const COLUMN_COUNT = 5

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/warehouse-positions/',
)({
    validateSearch: FILTERS.validateSearch,
    component: WarehousePositionsListPage,
})

function WarehousePositionsListPage() {
    const navigate = useNavigate({ from: '/warehouse-positions/' })

    const { values, barProps, page } = useListFilters({
        def: FILTERS,
        search: Route.useSearch(),
        onSearchChange: (next, opts) =>
            void navigate({
                search: () => next,
                replace: opts?.replace ?? false,
            }),
        columns: 4,
    })

    // Bung một vị trí tại một thời điểm, giống bảng tồn kho của báo cáo: mỗi
    // dòng bung là một request tồn, mở nhiều dòng là bắn cả nắm request.
    const [expandedId, setExpandedId] = useState<string | null>(null)

    // Đổi bộ lọc/trang là đổi hẳn tập dòng: giữ dòng đang bung sẽ để lại một
    // request sống cho vị trí không còn hiển thị, và khi quay lại bộ lọc cũ thì
    // dòng tự mở dù người dùng không bấm. Reset ngay trong lúc render (pattern
    // "adjust state on prop change" của React) và đặt TRƯỚC hook tải tồn để
    // lượt render bị huỷ không kịp hỏi tồn của dòng vừa bị lọc mất.
    const filterKey = `${page}|${values.lot_type}|${values.is_active}|${values.q}`
    const [prevFilterKey, setPrevFilterKey] = useState(filterKey)
    let expandedPositionId = expandedId
    if (prevFilterKey !== filterKey) {
        setPrevFilterKey(filterKey)
        setExpandedId(null)
        expandedPositionId = null
    }

    // enabled: !!id trong hook → không có dòng nào bung thì không gọi API.
    const {
        data: stock,
        isPending: isStockPending,
        isError: isStockError,
        refetch: refetchStock,
    } = useWarehousePositionStock(expandedPositionId ?? '')

    const { data, isLoading } = useWarehousePositions({
        page,
        per_page: 20,
        lot_type: values.lot_type === 'all' ? undefined : values.lot_type,
        is_active:
            values.is_active === 'all'
                ? undefined
                : values.is_active === 'active',
        search: values.q || undefined,
    })

    const rows = data?.data ?? []

    // Rỗng vì chưa có dữ liệu khác hẳn rỗng vì bộ lọc quá hẹp — trường hợp thứ
    // hai phải cho người dùng lối thoát ngay tại chỗ (R10).
    const emptyState = (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
            <span className="text-muted-foreground">
                {barProps.activeCount > 0 || values.q
                    ? m.list_empty_filtered()
                    : m.warehouse_position_empty()}
            </span>
            {barProps.activeCount > 0 || values.q ? (
                <ClearFilterButton active onClear={barProps.onClear} />
            ) : null}
        </div>
    )

    return (
        <div className="space-y-4 page-pad pb-24 md:space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex min-w-0 items-center gap-2">
                    <PageBackButton className="-my-2 md:-my-1" />
                    <h1 className="text-xl font-semibold">
                        {m.warehouse_position_list_title()}
                    </h1>
                </div>
                <Link to="/warehouse-positions/new">
                    <Button size="sm">
                        <Plus className="size-4 mr-1" />
                        {m.warehouse_position_new_title()}
                    </Button>
                </Link>
            </div>

            <ListFilterBar {...barProps} />

            <div className="overflow-x-auto rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.warehouse_position_code()}</TableHead>
                            <TableHead>{m.warehouse_position_name()}</TableHead>
                            <TableHead>
                                {m.warehouse_position_applicable_types()}
                            </TableHead>
                            <TableHead>
                                {m.warehouse_position_status_label()}
                            </TableHead>
                            <TableHead className="text-right">
                                {m.warehouse_position_actions()}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading && (
                            <TableRow>
                                <TableCell colSpan={COLUMN_COUNT}>
                                    {m.warehouse_position_loading()}
                                </TableCell>
                            </TableRow>
                        )}
                        {!isLoading && rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={COLUMN_COUNT}>
                                    {emptyState}
                                </TableCell>
                            </TableRow>
                        )}
                        {rows.map((p) => {
                            const isExpanded = expandedPositionId === p.id
                            const toggle = () =>
                                setExpandedId(isExpanded ? null : p.id)
                            return (
                                <Fragment key={p.id}>
                                    {/* Bấm cả dòng để bung (vùng bấm lớn trên
                                        máy trạm kho); nút mã là lối vào cho
                                        bàn phím/trình đọc màn hình. Sửa vị trí
                                        có nút riêng ở cột Thao tác. */}
                                    <TableRow
                                        data-testid="warehouse-position-row"
                                        className="cursor-pointer hover:bg-muted/50"
                                        onClick={toggle}
                                    >
                                        <TableCell className="font-mono">
                                            <button
                                                type="button"
                                                aria-expanded={isExpanded}
                                                className="flex items-center gap-1.5 text-left hover:underline"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    toggle()
                                                }}
                                            >
                                                <ChevronRight
                                                    aria-hidden
                                                    className={twJoin(
                                                        'size-4 shrink-0 text-muted-foreground transition-transform',
                                                        isExpanded &&
                                                            'rotate-90',
                                                    )}
                                                />
                                                {p.code}
                                            </button>
                                        </TableCell>
                                        <TableCell>{p.name ?? '—'}</TableCell>
                                        <TableCell>
                                            <div className="flex gap-1 flex-wrap">
                                                {p.applicable_lot_types.map(
                                                    (t) => (
                                                        <Badge
                                                            key={t}
                                                            variant="outline"
                                                        >
                                                            {t === 'product'
                                                                ? m.warehouse_position_type_product()
                                                                : m.warehouse_position_type_packaging()}
                                                        </Badge>
                                                    ),
                                                )}
                                                {p.is_return_shelf && (
                                                    <Badge variant="secondary">
                                                        {m.warehouse_position_return_shelf()}
                                                    </Badge>
                                                )}
                                                {p.is_pos_shelf && (
                                                    <Badge variant="secondary">
                                                        {m.warehouse_position_pos_shelf()}
                                                    </Badge>
                                                )}
                                                {p.is_defect_shelf && (
                                                    <Badge variant="destructive">
                                                        {m.warehouse_position_defect_shelf()}
                                                    </Badge>
                                                )}
                                                {p.is_defect_shelf &&
                                                    p.defect_allow_sale && (
                                                        <Badge variant="outline">
                                                            {m.warehouse_position_defect_selling_badge()}
                                                        </Badge>
                                                    )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                variant={
                                                    p.is_active
                                                        ? 'default'
                                                        : 'destructive'
                                                }
                                            >
                                                {p.is_active
                                                    ? m.warehouse_position_active()
                                                    : m.warehouse_position_inactive()}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                asChild
                                                size="sm"
                                                variant="ghost"
                                            >
                                                <Link
                                                    to="/warehouse-positions/$id"
                                                    params={{ id: p.id }}
                                                    aria-label={m.warehouse_position_edit_aria(
                                                        { code: p.code },
                                                    )}
                                                    onClick={(e) =>
                                                        e.stopPropagation()
                                                    }
                                                >
                                                    <Pencil
                                                        aria-hidden
                                                        className="size-4"
                                                    />
                                                    {m.warehouse_position_edit_short()}
                                                </Link>
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                    {isExpanded && (
                                        <PositionStockRows
                                            colSpan={COLUMN_COUNT}
                                            items={stock}
                                            isLoading={isStockPending}
                                            isError={isStockError}
                                            onRetry={() => {
                                                void refetchStock()
                                            }}
                                        />
                                    )}
                                </Fragment>
                            )
                        })}
                    </TableBody>
                </Table>
            </div>
        </div>
    )
}
